'use client'

import { type ComponentProps, type ReactNode, useMemo, useState } from 'react'
import { IconAlertCircle } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'

import { IconChangeQuantity, ProductQuantityInput } from 'modules/cart'
import {
  avabilityProductCheck,
  getProductSubstitutesById,
  ProductTypes
} from 'modules/products'
import { distributionCenterId } from 'shared/config/distributionCenter'
import Button from 'shared/ui/Button'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'

import ProfileSaleOrdersRepeatProduct from './ProfileSaleOrdersRepeatProduct'

import styles from './ProfileSaleOrdersRepeatProductCard.module.scss'

const Counter = ({ children }: { children: ReactNode }) => (
  <div className={styles.counter}>{children}</div>
)

export const StyledProductQuantityInput = ({
  className,
  ...props
}: ComponentProps<typeof ProductQuantityInput>) => (
  <ProductQuantityInput
    {...props}
    className={clsx(styles.productQuantityInput, className)}
  />
)

export type ChngeQuantityRepeatCb = (quantity: number) => number

export type OnReplaceProduct = {
  oldProduct: ProductTypes
  newProduct: ProductTypes
}

interface ProfileSaleOrdersRepeatProductCardProps {
  product: ProductTypes
  onChangeQuantity: (v: number | ChngeQuantityRepeatCb) => void
  quantity: number
  token: string | undefined
  onReplaceProduct: (payload: OnReplaceProduct) => void
  currentCartItemQuantity: number
  id: string
}

const ProfileSaleOrdersRepeatProductCard = ({
  product,
  onChangeQuantity,
  quantity,
  token,
  onReplaceProduct,
  currentCartItemQuantity,
  id
}: ProfileSaleOrdersRepeatProductCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalReplacementsOpen, setIsModalReplacementsOpen] = useState(false)
  const { isAvailable, quantityLimit } = useMemo(
    () => avabilityProductCheck(product),
    [product]
  )

  const { data: productSubstitutes } = useQuery<ProductTypes[]>({
    queryKey: ['reorder-substitutes', product.id, token],
    queryFn: async () => {

      const res = await getProductSubstitutesById({
        token,
        productId: product.id,
        distributionCenterId
      })

      return res.data.data
    },
    initialData: [],
    enabled: Boolean(!isAvailable && token)
  })

  if (quantity === 0) {
    return null
  }

  const isQuantityLimitMet = quantity + currentCartItemQuantity >= quantityLimit
  const isQuantityLimitExceeded =
    quantity + currentCartItemQuantity > quantityLimit

  const increment = () => {
    onChangeQuantity(prev => prev + 1)
  }
  const decrement = () => {
    onChangeQuantity(prev => {
      if (prev === 1 && isAvailable) {
        setIsModalOpen(true)

        return prev
      }

      return prev - 1
    })
  }

  const handleRemoveProduct = () => {
    if (isAvailable) {
      setIsModalOpen(true)
    } else {
      removeProduct()
    }
  }

  const removeProduct = () => {
    setIsModalOpen(false)
    onChangeQuantity(0)
  }

  const onClose = () => {
    setIsModalOpen(false)
  }

  const onCloseReplacements = () => {
    setIsModalReplacementsOpen(false)
  }

  const openReplacements = () => {
    setIsModalReplacementsOpen(true)
  }

  const handleReplaceProduct = (replaceProduct: ProductTypes) => {
    onReplaceProduct({
      oldProduct: product,
      newProduct: replaceProduct
    })
    onCloseReplacements()
  }

  const max = quantityLimit - currentCartItemQuantity

  return (
    <li
      className={clsx(styles.wrapper, {
        [styles.unavailable]: !(isAvailable && !isQuantityLimitExceeded)
      })}
      id={id}
    >
      <ProfileSaleOrdersRepeatProduct
        product={product}
        onRemoveProduct={handleRemoveProduct}
      />
      <Counter>
        <IconChangeQuantity
          type='minus'
          disabled={false}
          isLoading={false}
          onClick={decrement}
        />
        <StyledProductQuantityInput
          value={quantity}
          onChange={onChangeQuantity}
          quantityLimit={max}
          disabled={false}
          aria-label={`Ilość produktu ${product.name}`}
        />
        <IconChangeQuantity
          type='add'
          disabled={isQuantityLimitMet}
          isLoading={false}
          onClick={increment}
        />
      </Counter>
      {(!isAvailable || isQuantityLimitExceeded) && (
        <div className={styles.unavailableWrapper}>
          {!isAvailable ? (
            <div className={styles.unavailableContent}>
              <IconAlertCircle className={styles.unavailableIcon} />
              <p>Produkt niedostępny</p>
              {productSubstitutes && productSubstitutes.length > 0 && (
                <Button
                  variant='primary'
                  size='small'
                  onClick={openReplacements}
                >
                  Pokaż zamienniki
                </Button>
              )}
            </div>
          ) : (
            <div className={styles.unavailableContent}>
              <p>Maksymalna dostępna ilość to: {max}</p>
            </div>
          )}
        </div>
      )}

      <Modal
        id='confirm-remove-product-modal'
        onClose={onClose}
        open={isModalOpen}
        size='small'
      >
        <ModalTitle>Potwierdź usunięcie produktu</ModalTitle>
        <ModalDescription>
          Czy jesteś pewien, że chcesz usunąć <b>{product.name}</b> ze swojego
          koszyka?
        </ModalDescription>
        <div className={styles.modalButtonsWrapper}>
          <Button fullWidth variant='primary' onClick={removeProduct}>
            Potwierdź
          </Button>
          <Button fullWidth onClick={onClose} variant='neutralOutline'>
            Anuluj
          </Button>
        </div>
      </Modal>

      <Modal
        id='product-substitutes-modal'
        onClose={onCloseReplacements}
        open={isModalReplacementsOpen}
        title='Zamienniki produktu'
      >
        <ModalTitle>Zamienniki produktu</ModalTitle>
        <ModalDescription>
          Wybierz zamienniki produktu <b>{product.name} </b>
        </ModalDescription>
        <div className={styles.substitutesWrapper}>
          {productSubstitutes?.slice(0, 3).map(substitute => (
            <ProfileSaleOrdersRepeatProduct
              key={substitute.id}
              product={substitute}
              onReplaceProduct={handleReplaceProduct}
            />
          ))}
        </div>
      </Modal>
    </li>
  )
}

export default ProfileSaleOrdersRepeatProductCard
